import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";

import { X_DEFAULT_HASHTAGS } from "../constants";
import type { IXShareSelection } from "../types";

interface IXShareContextType {
  selection: IXShareSelection | null;
  setSelection: (selection: IXShareSelection) => void;
  draft: string | null;
  setDraft: (draft: string) => void;
  mention: boolean;
  setMention: (mention: boolean) => void;
  hashtagsEnabled: boolean;
  setHashtagsEnabled: (enabled: boolean) => void;
  hashtags: string;
  setHashtags: (hashtags: string) => void;
  chartCard: boolean;
  setChartCard: (chartCard: boolean) => void;
}

const XShareContext = createContext<IXShareContextType | null>(null);

export const XShareProvider = ({
  children,
  defaultSelection,
}: PropsWithChildren<{ defaultSelection: IXShareSelection | null }>) => {
  const [selection, setSelection] = useState(defaultSelection);
  const [draft, setDraft] = useState<string | null>(null);
  const [mention, setMention] = useState(true);
  const [hashtagsEnabled, setHashtagsEnabled] = useState(true);
  const [hashtags, setHashtags] = useState(X_DEFAULT_HASHTAGS);
  const [chartCard, setChartCard] = useState(true);

  const value = useMemo(
    () => ({
      selection,
      setSelection,
      draft,
      setDraft,
      mention,
      setMention,
      hashtagsEnabled,
      setHashtagsEnabled,
      hashtags,
      setHashtags,
      chartCard,
      setChartCard,
    }),
    [chartCard, draft, hashtags, hashtagsEnabled, mention, selection]
  );

  return (
    <XShareContext.Provider value={value}>{children}</XShareContext.Provider>
  );
};

export const useXShare = (): IXShareContextType => {
  const context = useContext(XShareContext);

  if (!context) {
    throw new Error("useXShare must be used within an XShareProvider");
  }

  return context;
};
